/** Mapa satélite con Leaflet + Esri (sin clave) si Google Maps JS no está disponible. */

let leafletPromise = null;

function loadCss(href) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`link[href="${href}"]`)) {
      resolve();
      return;
    }
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => reject(new Error("css_load_failed"));
    document.head.appendChild(link);
  });
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("script_load_failed"));
    document.head.appendChild(script);
  });
}

export async function loadLeaflet() {
  if (typeof window === "undefined") throw new Error("no_window");
  if (window.L) return window.L;
  if (!leafletPromise) {
    leafletPromise = Promise.all([
      loadCss("https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"),
      loadScript("https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"),
    ]).then(() => {
      if (!window.L) throw new Error("leaflet_unavailable");
      // Iconos por defecto: rutas absolutas (si no, el pin sale roto con Vite).
      try {
        // eslint-disable-next-line no-underscore-dangle
        delete window.L.Icon.Default.prototype._getIconUrl;
        window.L.Icon.Default.mergeOptions({
          iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
          iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
          shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
        });
      } catch {
        /* noop */
      }
      return window.L;
    });
  }
  return leafletPromise;
}

const ESRI_SATELLITE =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

/** Etiquetas de calles y topónimos sobre la vista satélite (modo híbrido). */
const CARTO_LABELS =
  "https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png";

/**
 * @param {HTMLElement} container
 * @param {{ lat: number, lng: number, zoom?: number, draggable?: boolean, onCoordsChange?: (lat,lng)=>void }} opts
 * @returns {{ map, marker, destroy: () => void }}
 */
export function createLeafletSatelliteMap(container, opts) {
  const L = window.L;
  const { lat, lng, zoom = 18, draggable = true, onCoordsChange } = opts;

  const map = L.map(container, { zoomControl: true, attributionControl: true }).setView([lat, lng], zoom);
  L.tileLayer(ESRI_SATELLITE, {
    attribution: "Tiles &copy; Esri",
    maxZoom: 19,
  }).addTo(map);
  L.tileLayer(CARTO_LABELS, {
    attribution: "&copy; CARTO",
    subdomains: "abcd",
    maxZoom: 20,
    opacity: 0.92,
  }).addTo(map);

  const marker = L.marker([lat, lng], { draggable }).addTo(map);

  if (draggable && onCoordsChange) {
    marker.on("dragend", () => {
      const p = marker.getLatLng();
      onCoordsChange(p.lat, p.lng);
    });
  }

  map.on("click", (e) => {
    if (!onCoordsChange) return;
    marker.setLatLng(e.latlng);
    onCoordsChange(e.latlng.lat, e.latlng.lng);
  });

  setTimeout(() => map.invalidateSize(), 80);

  return {
    map,
    marker,
    destroy() {
      map.remove();
    },
  };
}

const OSM_STREETS = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

/**
 * Mapa de calles (OpenStreetMap) con pin arrastrable.
 */
export function createLeafletStreetMap(container, opts) {
  const L = window.L;
  const { lat, lng, zoom = 14, draggable = true, onCoordsChange } = opts;

  const map = L.map(container, { zoomControl: true, attributionControl: true }).setView([lat, lng], zoom);
  L.tileLayer(OSM_STREETS, {
    attribution: "&copy; OpenStreetMap",
    maxZoom: 19,
  }).addTo(map);

  const marker = L.marker([lat, lng], { draggable }).addTo(map);

  if (draggable && onCoordsChange) {
    marker.on("dragend", () => {
      const p = marker.getLatLng();
      onCoordsChange(p.lat, p.lng);
    });
  }

  map.on("click", (e) => {
    if (!onCoordsChange) return;
    marker.setLatLng(e.latlng);
    onCoordsChange(e.latlng.lat, e.latlng.lng);
  });

  setTimeout(() => map.invalidateSize(), 80);

  return {
    map,
    marker,
    destroy() {
      map.remove();
    },
  };
}

/** Ortofoto PNOA (IGN) vía WMTS compatible GoogleMaps. */
const PNOA_WMTS =
  "https://www.ign.es/wmts/pnoa-ma?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=OI.OrthoimageCoverage&STYLE=default&FORMAT=image/jpeg&TILEMATRIXSET=GoogleMapsCompatible&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}";

const DPMT_WMS =
  "https://gis.miteco.gob.es/geoserver/costas/dominio_publico_maritimo_terrestre/wms";
const SP_WMS = "https://gis.miteco.gob.es/geoserver/costas/Servidumbre_Proteccion/wms";

/**
 * Mapa interactivo costas: ortofoto + capas WMS MITECO (DPMT / serv. protección) + pin.
 * Pan con arrastre; zoom con controles (+/− / lupa).
 * @param {HTMLElement} container
 * @param {{ lat: number, lng: number, zoom?: number, label?: string }} opts
 */
export function createLeafletCostasMap(container, opts) {
  const L = window.L;
  const { lat, lng, zoom = 18, label = "Vivienda" } = opts;

  const map = L.map(container, {
    zoomControl: false,
    attributionControl: true,
    maxZoom: 20,
  }).setView([lat, lng], zoom);

  L.control.zoom({ position: "bottomleft" }).addTo(map);

  L.tileLayer(ESRI_SATELLITE, {
    attribution: "Tiles © Esri",
    maxZoom: 19,
  }).addTo(map);

  L.tileLayer(PNOA_WMTS, {
    attribution: "© IGN PNOA",
    maxZoom: 20,
    maxNativeZoom: 19,
  }).addTo(map);

  L.tileLayer
    .wms(DPMT_WMS, {
      layers: "dominio_publico_maritimo_terrestre",
      styles: "linea_estilo_dpmt",
      format: "image/png",
      transparent: true,
      version: "1.3.0",
      attribution: "© MITECO DPMT",
      uppercase: true,
      maxZoom: 20,
    })
    .addTo(map);

  L.tileLayer
    .wms(SP_WMS, {
      layers: "Servidumbre_Proteccion",
      styles: "Servidumbre_Proteccion_estilo",
      format: "image/png",
      transparent: true,
      version: "1.3.0",
      attribution: "© MITECO SP",
      uppercase: true,
      maxZoom: 20,
    })
    .addTo(map);

  const marker = L.marker([lat, lng], { draggable: false }).addTo(map);
  if (label) {
    marker.bindTooltip(String(label), {
      permanent: true,
      direction: "bottom",
      offset: [0, 8],
    });
  }

  setTimeout(() => map.invalidateSize(), 80);
  setTimeout(() => map.invalidateSize(), 300);

  return {
    map,
    marker,
    destroy() {
      map.remove();
    },
  };
}
